import { computed, inject, ref, type App, type InjectionKey, type Ref } from "vue";

type BaseLocale = Record<string, Record<string, string>>;

type InferAvailableLang<T> = keyof T;
type InferAvailableKeys<T extends BaseLocale, TLang extends keyof T> = keyof T[TLang];

type LocaleApi<TLang> = {
	lang: Ref<TLang>;
};

export function createLocaleApi<
	const TLocale extends BaseLocale,
	TLang extends InferAvailableLang<TLocale>,
	TDefaultLang extends TLang,
>(locale: TLocale, defaultLang: TDefaultLang) {
	const id: InjectionKey<LocaleApi<TLang>> = Symbol();

	function useLocale() {
		const api = inject(id);

		const lang = computed(() => api?.lang.value || defaultLang);

		return {
			t(key: InferAvailableKeys<TLocale, TLang>): string {
				return locale[lang.value][key] ?? "";
			},
			setLang(lang: TLang) {
				if (!api) return;

				api.lang.value = lang;
			},
		};
	}

	function createLocale(app: App) {
		app.provide(id, {
			lang: ref(defaultLang as any),
		});
	}

	return { useLocale, createLocale };
}
